import { ArrowRight, ShieldCheck, Star } from "lucide-react";

const LOGO = "/brand/themboys-logo.jpg";

export default function Hero() {
  return (
    <section
      className="relative overflow-hidden bg-black border-b border-[#1f1f1f] min-h-[88vh] lg:min-h-[92vh] flex items-center"
      data-testid="hero"
    >
      {/* GIANT LOGO BACKGROUND — stretched, centered, fades to black at edges */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src={LOGO}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover scale-[1.15] opacity-[0.92]"
          style={{
            objectPosition: "center center",
          }}
        />
        {/* Radial vignette — fades logo edges into pure black */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at center, transparent 0%, transparent 35%, rgba(0,0,0,0.55) 65%, #000 95%)",
          }}
        />
        {/* Left-side darkening so headline text is readable */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.75) 25%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.15) 70%, transparent 100%)",
          }}
        />
        {/* Bottom fade for marquee transition */}
        <div
          className="absolute inset-x-0 bottom-0 h-32"
          style={{
            background: "linear-gradient(to bottom, transparent, #000)",
          }}
        />
        {/* Subtle brand-color glow */}
        <div
          className="absolute -right-32 top-1/2 -translate-y-1/2 w-[80%] aspect-square rounded-full opacity-[0.18] blur-3xl"
          style={{
            background:
              "radial-gradient(circle, #5BB239 0%, #4F8DFF 40%, transparent 70%)",
          }}
        />
      </div>

      {/* CONTENT */}
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-16 lg:py-24 w-full">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 bg-white/[0.06] backdrop-blur-sm px-3 py-1.5 border border-white/10">
            <span className="w-1.5 h-1.5 bg-[#5BB239]" />
            <span className="font-mono-label text-[10px] text-white/85">
              Kentucky Crew · KY · IN · OH · WV
            </span>
          </div>

          <h1 className="mt-7 font-display font-black text-white tracking-tight text-5xl sm:text-6xl lg:text-[88px] leading-[0.9] drop-shadow-2xl">
            Heavy lifting,
            <br />
            <span className="text-[#5BB239]">done right.</span>
          </h1>

          <p className="mt-7 max-w-xl text-base lg:text-lg text-white/80 leading-relaxed drop-shadow-lg">
            Locally owned in Louisville, KY. We pack, load, haul and store — local
            to your block or four-state region wide. Get an honest quote in 60 seconds.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <a
              href="#book"
              className="group inline-flex items-center gap-3 bg-[#5BB239] text-black px-7 py-4 font-bold uppercase tracking-wider text-sm hover:bg-[#499828] hover:text-white transition-colors shadow-2xl"
              data-testid="hero-book-btn"
            >
              Book My Move
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#quote"
              className="inline-flex items-center gap-3 bg-white text-black px-7 py-4 font-bold uppercase tracking-wider text-sm hover:bg-[#EBE8DD] transition-colors shadow-2xl"
              data-testid="hero-quote-btn"
            >
              Instant Quote
            </a>
            <a
              href="tel:+15025077904"
              className="inline-flex items-center gap-3 border border-white/25 backdrop-blur-sm bg-white/[0.04] text-white px-5 py-4 font-mono-label text-[11px] hover:bg-white/10 transition-colors"
              data-testid="hero-call-btn"
            >
              (502) 507-7904
            </a>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
            <Stat value="2,400+" label="Moves Completed" />
            <Stat value="4.9★" label="Google Rating" />
            <Stat value="2 hr" label="Avg Response" />
          </div>
        </div>

        {/* Floating trust badges anchored to the right (don't overlap headline) */}
        <div className="hidden lg:flex absolute right-8 bottom-12 flex-col gap-3 max-w-[240px]">
          <div className="bg-[#F5F3EC] text-[#151816] p-4 shadow-2xl rotate-[-2deg] backdrop-blur-sm">
            <div className="flex items-center gap-2 text-[#5BB239]">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span className="font-mono-label text-[9px]">Promise</span>
            </div>
            <div className="mt-1.5 font-display font-bold text-sm leading-tight">
              No surprise charges. Ever.
            </div>
          </div>
          <div className="bg-black/80 backdrop-blur border border-white/10 text-white p-4 shadow-2xl rotate-[1.5deg]">
            <div className="flex items-center gap-1 text-[#5BB239]">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-3 h-3 fill-current" />
              ))}
            </div>
            <p className="mt-2 text-[11px] leading-snug">
              "Best movers I've ever hired. Showed up early, wrapped everything, didn't scratch a wall."
            </p>
            <div className="mt-2 font-mono-label text-[9px] opacity-60">
              — TINA R., LOUISVILLE
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-display font-black text-3xl lg:text-4xl text-white tracking-tight drop-shadow-lg">
        {value}
      </div>
      <div className="font-mono-label text-[10px] text-white/55 mt-1">
        {label}
      </div>
    </div>
  );
}
